import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const AIReadinessWidget = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);

  const questions = [
    {
      id: 'ai_strategy',
      question: "Does your organization have a defined AI strategy?",
      options: [
        { value: 3, label: "Comprehensive strategy with roadmap" },
        { value: 2, label: "Basic strategy in development" },
        { value: 1, label: "Informal discussions only" },
        { value: 0, label: "No AI strategy" }
      ]
    },
    {
      id: 'data_infrastructure',
      question: "How would you rate your data infrastructure readiness?",
      options: [
        { value: 3, label: "Enterprise-grade, AI-ready infrastructure" },
        { value: 2, label: "Good infrastructure, some gaps" },
        { value: 1, label: "Basic infrastructure, needs improvement" },
        { value: 0, label: "Limited or outdated infrastructure" }
      ]
    },
    {
      id: 'technical_talent',
      question: "What\'s your current AI/ML technical talent situation?",
      options: [
        { value: 3, label: "Strong internal AI team" },
        { value: 2, label: "Some AI expertise, growing team" },
        { value: 1, label: "Limited AI skills, mostly external" },
        { value: 0, label: "No dedicated AI talent" }
      ]
    },
    {
      id: 'vendor_dependency',
      question: "How dependent are you on AI vendor solutions?",
      options: [
        { value: 0, label: "Heavily dependent on multiple vendors" },
        { value: 1, label: "Moderate vendor dependency" },
        { value: 2, label: "Some internal capabilities" },
        { value: 3, label: "Mostly internal AI capabilities" }
      ]
    }
  ];

  const handleAnswer = (value) => {
    const newAnswers = { ...answers, [questions?.[currentQuestion]?.id]: value };
    setAnswers(newAnswers);

    if (currentQuestion < questions?.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setShowResults(true);
    }
  };

  const calculateScore = () => {
    const totalScore = Object.values(answers)?.reduce((sum, score) => sum + score, 0);
    const maxScore = questions?.length * 3;
    return Math.round((totalScore / maxScore) * 100);
  };

  const getScoreLevel = (score) => {
    if (score >= 80) return { level: "AI Ready", color: "text-green-500", bg: "bg-green-50" };
    if (score >= 60) return { level: "Getting There", color: "text-yellow-600", bg: "bg-yellow-50" };
    if (score >= 40) return { level: "Early Stage", color: "text-orange-500", bg: "bg-orange-50" };
    return { level: "Starting Out", color: "text-red-500", bg: "bg-red-50" };
  };

  const resetAssessment = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
  };

  if (showResults) {
    const score = calculateScore();
    const scoreLevel = getScoreLevel(score);

    return (
      <section className="py-20 bg-gradient-to-br from-brand-surface to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-executive p-8 lg:p-12">
            <div className="text-center space-y-6">
              <div className="w-20 h-20 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-full flex items-center justify-center mx-auto">
                <Icon name="Target" size={32} className="text-white" />
              </div>

              <div>
                <h2 className="text-3xl font-bold text-brand-primary mb-2">
                  Your AI Readiness Score
                </h2>
                <div className="text-6xl font-bold text-brand-secondary mb-4">
                  {score}%
                </div>
                <div className={`inline-flex items-center px-4 py-2 rounded-full ${scoreLevel?.bg} ${scoreLevel?.color} font-semibold`}>
                  {scoreLevel?.level}
                </div>
              </div>

              <div className="max-w-2xl mx-auto">
                <p className="text-text-secondary leading-relaxed mb-8">
                  {score >= 80 && "Excellent! You're well-positioned for AI independence. Let's discuss advanced strategies to maximize your competitive advantage."}
                  {score >= 60 && score < 80 && "Good progress! You have solid foundations. Focus on reducing vendor dependencies and building internal capabilities."}
                  {score >= 40 && score < 60 && "You're on the right track but have significant opportunities. Our AI Core Blueprint can accelerate your journey."}
                  {score < 40 && "Great timing to start your AI transformation! Our proven methodology will help you build from a strong foundation."}
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    variant="default"
                    size="lg"
                    className="bg-brand-primary hover:bg-brand-primary/90"
                    iconName="Download"
                    iconPosition="right"
                  >
                    Get Personalized Blueprint
                  </Button>
                  
                  <Button 
                    variant="outline"
                    size="lg"
                    onClick={resetAssessment}
                    iconName="RotateCcw"
                    iconPosition="left"
                  >
                    Retake Assessment
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gradient-to-br from-brand-surface to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-brand-primary mb-4">
            AI Readiness Pulse Check
          </h2>
          <p className="text-xl text-text-secondary">
            Discover your organization's AI independence potential in 2 minutes
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-executive p-8 lg:p-12">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between text-sm text-text-secondary mb-2">
              <span>Question {currentQuestion + 1} of {questions?.length}</span>
              <span>{Math.round(((currentQuestion + 1) / questions?.length) * 100)}% Complete</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-brand-secondary h-2 rounded-full transition-all duration-300"
                style={{ width: `${((currentQuestion + 1) / questions?.length) * 100}%` }}
              ></div>
            </div>
          </div>

          {/* Question */}
          <div className="space-y-8">
            <h3 className="text-2xl font-semibold text-brand-primary text-center">
              {questions?.[currentQuestion]?.question}
            </h3>

            {/* Answer Options */}
            <div className="space-y-4">
              {questions?.[currentQuestion]?.options?.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswer(option?.value)}
                  className="w-full p-4 text-left border border-gray-200 rounded-xl hover:border-brand-secondary hover:bg-brand-surface transition-all duration-200 group"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-text-primary group-hover:text-brand-primary font-medium">
                      {option?.label}
                    </span>
                    <Icon name="ChevronRight" size={20} className="text-gray-400 group-hover:text-brand-secondary" />
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Navigation */}
          {currentQuestion > 0 && (
            <div className="mt-8 flex justify-center">
              <Button
                variant="ghost"
                onClick={() => setCurrentQuestion(currentQuestion - 1)}
                iconName="ChevronLeft"
                iconPosition="left"
              >
                Previous Question
              </Button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default AIReadinessWidget;